import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import "./personalData.css";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import MobileNav from "../../components/mobileNav/MobileNav";

// Define available regions with their IDs
const AVAILABLE_REGIONS = [
  { id: 1, name: "الرياض" }, // Riyadh
  { id: 2, name: "القصيم" }, // Qassim
];

// Validation functions
const validateName = (name) => {
  if (!name.trim()) return "الاسم مطلوب";
  if (name.length < 3) return "الاسم يجب أن يكون على الأقل 3 أحرف";
  return "";
};

const validateEmail = (email) => {
  if (!email.trim()) return "البريد الإلكتروني مطلوب";
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) return "البريد الإلكتروني غير صالح";
  return "";
};

const validateMobile = (mobile) => {
  if (!mobile.trim()) return "رقم الجوال مطلوب";
  const mobileRegex = /^05\d{8}$/;
  if (!mobileRegex.test(mobile))
    return "رقم الجوال يجب أن يبدأ بـ 05 ويتكون من 10 أرقام";
  return "";
};

const validateRegions = (regions) => {
  if (regions.length === 0) return "يجب اختيار منطقة واحدة على الأقل";
  return "";
};

const PersonalData = () => {
  const [editMode, setEditMode] = useState({
    name: false,
    email: false,
    mobile: false,
    region: false,
    image: false,
  });

  const [userData, setUserData] = useState({
    name: "",
    email: "",
    mobile: "",
    regions: [],
    regionIds: [],
    image: "https://via.placeholder.com/150",
    title: "",
    userName: "",
    isActive: true,
  });

  const [tempData, setTempData] = useState({ ...userData });
  const [validationErrors, setValidationErrors] = useState({
    name: "",
    email: "",
    mobile: "",
    region: "",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [imagePreview, setImagePreview] = useState("");
  const fileInputRef = useRef(null);

  const params = useParams();
  const userId = params.personalDataId;

  const DEFAULT_PROFILE_IMAGE =
    "https://cdn-icons-png.flaticon.com/512/3135/3135715.png";

  // Helper function to render field value with placeholder style
  const renderFieldValue = (value) => {
    return value ? (
      <span>{value}</span>
    ) : (
      <span className="placeholder-text">No data available</span>
    );
  };

  // Fetch user data from API
  const fetchUserData = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `https://api.generalhome.sa/api/Authenticate/GetUserById?userId=${userId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const user = response.data;

      const userRegions =
        user.Areas?.map((area) => area.MaintenanceAreaName) || [];
      const userRegionIds =
        user.Areas?.map((area) => area.MaintenanceAreaId) || [];
      const roleName = user.Roles?.[0] || "";

      setUserData({
        name: user.FullName || "",
        email: user.Email || "",
        mobile: user.Mobile || user.PhoneNumber || "",
        regions: userRegions,
        regionIds: userRegionIds,
        image: user.Image || user.ImageUrl || DEFAULT_PROFILE_IMAGE,
        title: roleName,
        userName: user.UserName || "",
        isActive: user.IsActive ?? true,
      });

      setTempData({
        name: user.FullName || "",
        email: user.Email || "",
        mobile: user.Mobile || user.PhoneNumber || "",
        regions: userRegions,
        regionIds: userRegionIds,
        image: user.ImageUrl || DEFAULT_PROFILE_IMAGE,
        userName: user.UserName || "",
      });
    } catch (error) {
      toast.error("فشل في تحميل بيانات المستخدم");
      console.error("Error fetching user data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  // Update user data via API
  const updateUserData = async (field, value) => {
    try {
      setIsLoading(true);

      // Validate the field before saving
      if (field === "name") {
        const error = validateName(value);
        if (error) {
          setValidationErrors((prev) => ({ ...prev, name: error }));
          return;
        }
      } else if (field === "email") {
        const error = validateEmail(value);
        if (error) {
          setValidationErrors((prev) => ({ ...prev, email: error }));
          return;
        }
      } else if (field === "mobile") {
        const error = validateMobile(value);
        if (error) {
          setValidationErrors((prev) => ({ ...prev, mobile: error }));
          return;
        }
      } else if (field === "region") {
        const error = validateRegions(tempData.regions);
        if (error) {
          setValidationErrors((prev) => ({ ...prev, region: error }));
          return;
        }
      }

      let payload = {
        UserId: userId,
        FullName: field === "name" ? value : userData.name,
        Email: field === "email" ? value : userData.email,
        PhoneNumber: field === "mobile" ? value : userData.mobile,
        Mobile: field === "mobile" ? value : userData.mobile,
        UserName: userData.userName,
        IsActive: userData.isActive,
        MaintenanceAreaIds:
          field === "region" ? tempData.regionIds : userData.regionIds,
        Image: userData.image,
      };

      // Special case for image upload
      if (field === "image") {
        const formData = new FormData();
        formData.append("file", value);

        try {
          const response = await axios.post(
            `https://api.generalhome.sa/api/Themes/UploadImages`,
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );

          const imageUrl = response.data?.imageUrl || response.data;
          if (!imageUrl) throw new Error("Failed to upload image");

          const fullImageUrl = imageUrl.startsWith("http")
            ? imageUrl
            : `https://api.generalhome.sa${
                imageUrl.startsWith("/") ? "" : "/"
              }${imageUrl}`;

          // Now update the user with the new image URL
          await axios.put(
            `https://api.generalhome.sa/api/Authenticate/UpdateUser`,
            {
              ...payload,
              Image: fullImageUrl,
            },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
                "Content-Type": "application/json",
              },
            }
          );

          setUserData((prev) => ({ ...prev, image: fullImageUrl }));

          toast.success("تم تحديث الصورة بنجاح");
        } catch (error) {
          console.error("Error uploading image:", error);
          if (error.response?.status === 401) {
            toast.error("انتهت جلستك، برجاء تسجيل الدخول مرة أخرى");
          } else {
            toast.error("فشل رفع الصورة، برجاء المحاولة مرة أخرى");
          }
          throw error;
        }
      } else {
        // Update regular fields
        await axios.put(
          `https://api.generalhome.sa/api/Authenticate/UpdateUser`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          }
        );
        toast.success("تم تحديث البيانات بنجاح");
      }

      setUserData((prev) => ({
        ...prev,
        [field]: field === "region" ? tempData.regions : value,
        regionIds: field === "region" ? tempData.regionIds : prev.regionIds,
      }));

      fetchUserData(); // Refresh data after update
      setValidationErrors({
        name: "",
        email: "",
        mobile: "",
        region: "",
      });
    } catch (error) {
      toast.error("فشل في تحديث البيانات");
      console.error("Error updating user data:", error);
      // Revert to previous value on error
      if (field !== "image") {
        setUserData((prev) => ({ ...prev, [field]: tempData[field] }));
      } else {
        setImagePreview("");
      }
    } finally {
      setIsLoading(false);
      setEditMode({ ...editMode, [field]: false });
    }
  };

  const handleRegionChange = (e, region) => {
    const regionId = AVAILABLE_REGIONS.find((r) => r.name === region)?.id;
    if (!regionId) return;

    setTempData((prev) => {
      const newRegionIds = e.target.checked
        ? [...prev.regionIds, regionId]
        : prev.regionIds.filter((id) => id !== regionId);

      const newRegions = e.target.checked
        ? [...prev.regions, region]
        : prev.regions.filter((r) => r !== region);

      return {
        ...prev,
        regionIds: newRegionIds,
        regions: newRegions,
      };
    });

    // Clear region validation error when user makes a selection
    if (e.target.checked) {
      setValidationErrors((prev) => ({ ...prev, region: "" }));
    }
  };

  const renderRegions = () => {
    if (!userData.regions.length)
      return <span className="placeholder-text">No region specified</span>;
    return userData.regions.join(", ");
  };

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  const handleEditClick = (field) => {
    if (field === "image") {
      fileInputRef.current.click();
    } else {
      setEditMode({ ...editMode, [field]: true });
      // Clear validation error when entering edit mode
      setValidationErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleSaveClick = async (field) => {
    // Validate before saving
    if (field === "region" || tempData[field] !== userData[field]) {
      if (field === "region") {
        const error = validateRegions(tempData.regions);
        if (error) {
          setValidationErrors((prev) => ({ ...prev, region: error }));
          return;
        }
      } else {
        const error = validateField(field, tempData[field]);
        if (error) {
          setValidationErrors((prev) => ({ ...prev, [field]: error }));
          return;
        }
      }
      await updateUserData(field, tempData[field]);
    }
    setEditMode({ ...editMode, [field]: false });
  };

  const validateField = (field, value) => {
    switch (field) {
      case "name":
        return validateName(value);
      case "email":
        return validateEmail(value);
      case "mobile":
        return validateMobile(value);
      case "region":
        return validateRegions(value);
      default:
        return "";
    }
  };

  const handleCancelClick = (field) => {
    if (field === "image") {
      setImagePreview("");
    } else {
      setTempData({ ...tempData, [field]: userData[field] });
    }
    setEditMode({ ...editMode, [field]: false });
    // Clear validation error when canceling
    setValidationErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleInputChange = (e, field) => {
    const value = e.target.value;
    setTempData({ ...tempData, [field]: value });

    // Validate on change and clear error if valid
    const error = validateField(field, value);
    if (!error) {
      setValidationErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate image file
      if (!file.type.startsWith("image/")) {
        toast.error("الرجاء اختيار ملف صورة فقط");
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        toast.error("حجم الصورة يجب أن يكون أقل من 2MB");
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setTempData({ ...tempData, image: file });
        setEditMode({ ...editMode, image: true });
      };
      reader.readAsDataURL(file);
    }
  };

  if (isLoading && !userData.name) {
    return (
      <div className="personalData">
        <div className="personalData-container container">
          <div className="loading-spinner">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">جار التحميل...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <MobileNav />
      <div className="personalData">
        <div className="personalData-container container">
          {/* Profile Image and Name */}
          <div className="profile-header">
            <div className="profile-image-container">
              <img
                src={
                  editMode.image && imagePreview
                    ? imagePreview
                    : userData.image || DEFAULT_PROFILE_IMAGE
                }
                alt="Profile"
                className="profile-image"
                onError={(e) => {
                  e.target.src = DEFAULT_PROFILE_IMAGE;
                }}
              />
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageChange}
                accept="image/*"
                style={{ display: "none" }}
              />
              <div className="image-edit-overlay">
                <button
                  className="edit-image-btn"
                  onClick={() => handleEditClick("image")}
                  disabled={isLoading}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="26"
                    height="26"
                    viewBox="0 0 26 26"
                    fill="none"
                  >
                    <path
                      d="M13.7895 2.26318H7.82105C5.43346 2.26318 4.23966 2.26318 3.32773 2.72784C2.52556 3.13656 1.87338 3.78874 1.46466 4.59091C1 5.50284 1 6.69664 1 9.08424V18.179C1 20.5666 1 21.7603 1.46466 22.6723C1.87338 23.4745 2.52556 24.1266 3.32773 24.5353C4.23966 25 5.43346 25 7.82105 25H16.9158C19.3034 25 20.4971 25 21.4092 24.5353C22.2113 24.1266 22.8635 23.4745 23.2722 22.6723C23.7368 21.7603 23.7368 20.5666 23.7368 18.179V12.2106"
                      stroke="white"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M1 19.4519L7.10042 13.4121C7.65539 12.8626 8.55514 12.8626 9.1101 13.4121L13.7895 18.0449M13.7895 18.0449L17.7583 14.1155C18.3133 13.5661 19.2131 13.5661 19.768 14.1155L23.7368 18.0449M13.7895 18.0449L16.9868 21.2105"
                      stroke="white"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M21.5263 1V4.47368M21.5263 4.47368V7.94737M21.5263 4.47368H18.0526M21.5263 4.47368H25"
                      stroke="white"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>
              </div>
              {editMode.image && (
                <div className="image-action-btns">
                  <button
                    className="save-btn"
                    onClick={() => updateUserData("image", tempData.image)}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <i className="fas fa-spinner fa-spin"></i>
                    ) : (
                      <i className="fas fa-check"></i>
                    )}
                  </button>
                  <button
                    className="cancel-btn"
                    onClick={() => handleCancelClick("image")}
                    disabled={isLoading}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              )}
            </div>
            <h2 className="profile-name">
              {renderFieldValue(userData.userName)}
            </h2>
            <p className="profile-title">{renderFieldValue(userData.title)}</p>
            <p className="profile-email">{renderFieldValue(userData.email)}</p>
          </div>

          {/* Information Cards */}
          <div className="info-cards">
            {/* Name Card */}
            <div className="info-card">
              <div className="card-header">
                <h3>الاسم</h3>
                {!editMode.name ? (
                  <button
                    className="edit-btn"
                    onClick={() => handleEditClick("name")}
                    disabled={isLoading}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="42"
                      height="42"
                      viewBox="0 0 42 42"
                      fill="none"
                    >
                      <path
                        d="M32.0457 13.7283L20.0249 25.7472C18.8279 26.9441 15.2745 27.4984 14.4807 26.7047C13.6869 25.911 14.2287 22.3582 15.4257 21.1614L27.4591 9.12979C27.7559 8.80608 28.1151 8.54587 28.5153 8.36483C28.9154 8.18379 29.3481 8.08565 29.7872 8.0764C30.2262 8.06717 30.6627 8.14697 31.07 8.31101C31.4774 8.47506 31.8474 8.71997 32.1575 9.03091C32.4676 9.34184 32.7115 9.71236 32.8744 10.1201C33.0374 10.5279 33.1161 10.9644 33.1056 11.4034C33.0952 11.8424 32.9958 12.2748 32.8137 12.6744C32.6315 13.0739 32.3703 13.4325 32.0457 13.7283Z"
                        stroke="#132239"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M19.6066 11.0757H13.2177C11.8621 11.0757 10.5622 11.5908 9.60365 12.5076C8.64514 13.4245 8.10657 14.668 8.10657 15.9646V28.1868C8.10657 29.4835 8.64514 30.7269 9.60365 31.6437C10.5622 32.5606 11.8621 33.0757 13.2177 33.0757H27.2732C30.0971 33.0757 31.1066 30.8757 31.1066 28.1868V22.0757"
                        stroke="#132239"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                ) : (
                  <div className="action-btns">
                    <button
                      className="save-btn"
                      onClick={() => handleSaveClick("name")}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <i className="fas fa-spinner fa-spin"></i>
                      ) : (
                        <i className="fas fa-check"></i>
                      )}
                    </button>
                    <button
                      className="cancel-btn"
                      onClick={() => handleCancelClick("name")}
                      disabled={isLoading}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                )}
              </div>
              {!editMode.name ? (
                <p className="card-value">{renderFieldValue(userData.name)}</p>
              ) : (
                <div className="edit-input-container">
                  <input
                    type="text"
                    value={tempData.name}
                    onChange={(e) => handleInputChange(e, "name")}
                    className={`edit-input ${
                      validationErrors.name ? "error" : ""
                    }`}
                    disabled={isLoading}
                    placeholder="أدخل الاسم"
                  />
                  {validationErrors.name && (
                    <span className="validation-error">
                      {validationErrors.name}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Email Card */}
            <div className="info-card">
              <div className="card-header">
                <h3>البريد الإلكتروني</h3>
                {!editMode.email ? (
                  <button
                    className="edit-btn"
                    onClick={() => handleEditClick("email")}
                    disabled={isLoading}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="42"
                      height="42"
                      viewBox="0 0 42 42"
                      fill="none"
                    >
                      <path
                        d="M32.0457 13.7283L20.0249 25.7472C18.8279 26.9441 15.2745 27.4984 14.4807 26.7047C13.6869 25.911 14.2287 22.3582 15.4257 21.1614L27.4591 9.12979C27.7559 8.80608 28.1151 8.54587 28.5153 8.36483C28.9154 8.18379 29.3481 8.08565 29.7872 8.0764C30.2262 8.06717 30.6627 8.14697 31.07 8.31101C31.4774 8.47506 31.8474 8.71997 32.1575 9.03091C32.4676 9.34184 32.7115 9.71236 32.8744 10.1201C33.0374 10.5279 33.1161 10.9644 33.1056 11.4034C33.0952 11.8424 32.9958 12.2748 32.8137 12.6744C32.6315 13.0739 32.3703 13.4325 32.0457 13.7283Z"
                        stroke="#132239"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M19.6066 11.0757H13.2177C11.8621 11.0757 10.5622 11.5908 9.60365 12.5076C8.64514 13.4245 8.10657 14.668 8.10657 15.9646V28.1868C8.10657 29.4835 8.64514 30.7269 9.60365 31.6437C10.5622 32.5606 11.8621 33.0757 13.2177 33.0757H27.2732C30.0971 33.0757 31.1066 30.8757 31.1066 28.1868V22.0757"
                        stroke="#132239"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                ) : (
                  <div className="action-btns">
                    <button
                      className="save-btn"
                      onClick={() => handleSaveClick("email")}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <i className="fas fa-spinner fa-spin"></i>
                      ) : (
                        <i className="fas fa-check"></i>
                      )}
                    </button>
                    <button
                      className="cancel-btn"
                      onClick={() => handleCancelClick("email")}
                      disabled={isLoading}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                )}
              </div>
              {!editMode.email ? (
                <p className="card-value">{renderFieldValue(userData.email)}</p>
              ) : (
                <div className="edit-input-container">
                  <input
                    type="email"
                    value={tempData.email}
                    onChange={(e) => handleInputChange(e, "email")}
                    className={`edit-input ${
                      validationErrors.email ? "error" : ""
                    }`}
                    disabled={isLoading}
                    placeholder="أدخل البريد الإلكتروني"
                  />
                  {validationErrors.email && (
                    <span className="validation-error">
                      {validationErrors.email}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Mobile Card */}
            <div className="info-card">
              <div className="card-header">
                <h3>رقم الجوال</h3>
                {!editMode.mobile ? (
                  <button
                    className="edit-btn"
                    onClick={() => handleEditClick("mobile")}
                    disabled={isLoading}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="42"
                      height="42"
                      viewBox="0 0 42 42"
                      fill="none"
                    >
                      <path
                        d="M32.0457 13.7283L20.0249 25.7472C18.8279 26.9441 15.2745 27.4984 14.4807 26.7047C13.6869 25.911 14.2287 22.3582 15.4257 21.1614L27.4591 9.12979C27.7559 8.80608 28.1151 8.54587 28.5153 8.36483C28.9154 8.18379 29.3481 8.08565 29.7872 8.0764C30.2262 8.06717 30.6627 8.14697 31.07 8.31101C31.4774 8.47506 31.8474 8.71997 32.1575 9.03091C32.4676 9.34184 32.7115 9.71236 32.8744 10.1201C33.0374 10.5279 33.1161 10.9644 33.1056 11.4034C33.0952 11.8424 32.9958 12.2748 32.8137 12.6744C32.6315 13.0739 32.3703 13.4325 32.0457 13.7283Z"
                        stroke="#132239"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M19.6066 11.0757H13.2177C11.8621 11.0757 10.5622 11.5908 9.60365 12.5076C8.64514 13.4245 8.10657 14.668 8.10657 15.9646V28.1868C8.10657 29.4835 8.64514 30.7269 9.60365 31.6437C10.5622 32.5606 11.8621 33.0757 13.2177 33.0757H27.2732C30.0971 33.0757 31.1066 30.8757 31.1066 28.1868V22.0757"
                        stroke="#132239"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                ) : (
                  <div className="action-btns">
                    <button
                      className="save-btn"
                      onClick={() => handleSaveClick("mobile")}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <i className="fas fa-spinner fa-spin"></i>
                      ) : (
                        <i className="fas fa-check"></i>
                      )}
                    </button>
                    <button
                      className="cancel-btn"
                      onClick={() => handleCancelClick("mobile")}
                      disabled={isLoading}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                )}
              </div>
              {!editMode.mobile ? (
                <p className="card-value">
                  {renderFieldValue(userData.mobile)}
                </p>
              ) : (
                <div className="edit-input-container">
                  <input
                    type="tel"
                    value={tempData.mobile}
                    onChange={(e) => handleInputChange(e, "mobile")}
                    className={`edit-input ${
                      validationErrors.mobile ? "error" : ""
                    }`}
                    disabled={isLoading}
                    placeholder="أدخل رقم الجوال"
                  />
                  {validationErrors.mobile && (
                    <span className="validation-error">
                      {validationErrors.mobile}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Region Card */}
            <div className="info-card">
              <div className="card-header">
                <h3>المنطقة</h3>
                {!editMode.region ? (
                  <button
                    className="edit-btn"
                    onClick={() => handleEditClick("region")}
                    disabled={isLoading}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="42"
                      height="42"
                      viewBox="0 0 42 42"
                      fill="none"
                    >
                      <path
                        d="M32.0457 13.7283L20.0249 25.7472C18.8279 26.9441 15.2745 27.4984 14.4807 26.7047C13.6869 25.911 14.2287 22.3582 15.4257 21.1614L27.4591 9.12979C27.7559 8.80608 28.1151 8.54587 28.5153 8.36483C28.9154 8.18379 29.3481 8.08565 29.7872 8.0764C30.2262 8.06717 30.6627 8.14697 31.07 8.31101C31.4774 8.47506 31.8474 8.71997 32.1575 9.03091C32.4676 9.34184 32.7115 9.71236 32.8744 10.1201C33.0374 10.5279 33.1161 10.9644 33.1056 11.4034C33.0952 11.8424 32.9958 12.2748 32.8137 12.6744C32.6315 13.0739 32.3703 13.4325 32.0457 13.7283Z"
                        stroke="#132239"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M19.6066 11.0757H13.2177C11.8621 11.0757 10.5622 11.5908 9.60365 12.5076C8.64514 13.4245 8.10657 14.668 8.10657 15.9646V28.1868C8.10657 29.4835 8.64514 30.7269 9.60365 31.6437C10.5622 32.5606 11.8621 33.0757 13.2177 33.0757H27.2732C30.0971 33.0757 31.1066 30.8757 31.1066 28.1868V22.0757"
                        stroke="#132239"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                ) : (
                  <div className="action-btns">
                    <button
                      className="save-btn"
                      onClick={() => handleSaveClick("region")}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <i className="fas fa-spinner fa-spin"></i>
                      ) : (
                        <i className="fas fa-check"></i>
                      )}
                    </button>
                    <button
                      className="cancel-btn"
                      onClick={() => handleCancelClick("region")}
                      disabled={isLoading}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                )}
              </div>
              {!editMode.region ? (
                <p className="card-value">{renderRegions()}</p>
              ) : (
                <div className="region-edit-container">
                  {AVAILABLE_REGIONS.map((region) => (
                    <div key={region.id} className="region-checkbox">
                      <input
                        type="checkbox"
                        id={`region-${region.id}`}
                        checked={tempData.regions.includes(region.name)}
                        onChange={(e) => handleRegionChange(e, region.name)}
                        disabled={isLoading}
                      />
                      <label htmlFor={`region-${region.id}`}>
                        {region.name}
                      </label>
                    </div>
                  ))}
                  {validationErrors.region && (
                    <span className="validation-error">
                      {validationErrors.region}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PersonalData;
